import { Button } from "@mui/material";
import { MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import { BrowseDialogViewMode } from "../app/slices/GlobalSlice";
import PiInputField from "./PiInputField";
import PiKeycap from "./PiKeycap";
import PiViewSwitcher from "./PiViewSwitcher";

export interface PiBrowseLegendItem {
  keys: string[];
  label: string;
}

interface PiBrowseLayoutProps {
  /** Search row. */
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  /** Lets "/" (bound by the caller's list hook) find the field. */
  searchInputRef?: MutableRefObject<HTMLInputElement | null>;
  /**
   * How long the field waits before committing a query. The default suits a
   * client-side filter, where a commit costs nothing; a dialog whose search is
   * server-backed should pass more so it does not fire a request per keystroke.
   */
  searchDebounceMs?: number;
  /** Onboarding tours anchor to the field by id. */
  searchFieldId?: string;
  /** e2e hooks — the layout root and the search wrapper. */
  dataTestId?: string;
  searchTestId?: string;
  /**
   * Ties the search field to the results for assistive tech. Focus never
   * leaves the field, so `activeDescendantId` is the only thing that tells a
   * screen reader which item the highlight is on. Pass both or neither.
   */
  listboxId?: string;
  activeDescendantId?: string;
  /**
   * Optional labelled scope control in the search row (e.g. the prop
   * library's Props/Situations switch), rendered between the "/" keycap and
   * the view switcher, as the frames draw it.
   */
  scopeSwitcher?: React.ReactNode;
  /**
   * View switch, persisted globally across the browse dialogs. Omit both when
   * the dialog has only one sensible view — the localize dialog lists
   * languages, and a grid of near-identical thumbnails says nothing a row
   * does not. With no switcher the legend drops to `↑ ↓`.
   */
  viewMode?: BrowseDialogViewMode;
  onViewModeChange?: (mode: BrowseDialogViewMode) => void;
  /**
   * Full-width strip between the search row and the columns, for a standing
   * condition the results cannot express on their own — e.g. "only images can
   * be selected". Not for transient feedback; it never dismisses.
   */
  notice?: React.ReactNode;
  /** Columns. `rail` and `details` are optional; `results` is the middle. */
  rail?: React.ReactNode;
  results: React.ReactNode;
  details?: React.ReactNode;
  /** Absolutely-positioned extras — onboarding buttons and overlays. */
  overlay?: React.ReactNode;
  /**
   * Appended after the standard legend (move keys per view, Esc close) —
   * e.g. `Space · Select` when multi-select is active.
   */
  legendExtras?: PiBrowseLegendItem[];
  /**
   * Footer primary action, carrying the bare ↵ Enter affordance. Omit both
   * when the dialog has nothing to confirm — a file explorer opened to browse
   * and manage rather than to pick has no "use this one" to offer, and a
   * button that does nothing is worse than no button.
   */
  primaryLabel?: string;
  primaryDisabled?: boolean;
  onPrimaryAction?: () => void;
}

/**
 * The inner three-row layout every browse dialog shares: search row ·
 * rail/results/details columns · footer. The footer legend only ever lists
 * keys that do something in the current view — `← ↑ ↓ →` in tiles, `↑ ↓` in
 * list.
 */
export default function PiBrowseLayout(props: PiBrowseLayoutProps) {
  const {
    searchValue,
    onSearchChange,
    searchPlaceholder,
    searchInputRef,
    searchDebounceMs = 120,
    searchFieldId,
    dataTestId,
    searchTestId,
    listboxId,
    activeDescendantId,
    scopeSwitcher,
    viewMode,
    onViewModeChange,
    notice,
    rail,
    results,
    details,
    overlay,
    legendExtras = [],
    primaryLabel,
    primaryDisabled = false,
    onPrimaryAction,
  } = props;
  const { t } = useTranslation();

  const legend: PiBrowseLegendItem[] = [
    {
      // No view mode means list-only, which wants the same two keys.
      keys: viewMode === "tile" ? ["←", "↑", "↓", "→"] : ["↑", "↓"],
      label: t("browse_dialog.legend_move"),
    },
    ...legendExtras,
    { keys: ["Esc"], label: t("browse_dialog.legend_close") },
  ];

  return (
    // flex-1 + min-h-0, not h-full: the parent (pidialog-body) is a column
    // flex container whose own height comes from flexing, and Chrome resolves
    // a child's percentage height against `auto` there — the layout grows to
    // its content and the footer lands below the fold.
    <div
      className="flex relative flex-col flex-1 min-h-0 w-full"
      data-testid={dataTestId}
    >
      <div className="shrink-0 h-[52px] border-b border-creator-border px-[16px] flex items-center gap-[12px]">
        <div
          className="flex-1 min-w-0 pibrowsedialog-search"
          data-testid={searchTestId}
        >
          <PiInputField
            label=""
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={onSearchChange}
            id={searchFieldId}
            inputRef={searchInputRef}
            controlsId={listboxId}
            activeDescendantId={activeDescendantId}
            showSearchIcon
            debounceTime={searchDebounceMs}
            autoFocus
          />
        </div>

        {/* Discoverable without spending a slot in the footer legend. */}
        <PiKeycap>/</PiKeycap>

        {scopeSwitcher}

        {viewMode && onViewModeChange && (
          <PiViewSwitcher value={viewMode} onChange={onViewModeChange} />
        )}
      </div>

      {notice}

      <div className="flex-1 min-h-0 flex">
        {rail}
        <div className="flex-1 min-w-0 flex flex-col">{results}</div>
        {details}
      </div>

      <div className="h-[56px] shrink-0 border-t border-creator-border px-[16px] flex items-center justify-between gap-[16px]">
        <div className="flex items-center gap-[14px] min-w-0">
          {legend.map((item) => (
            <div key={item.label} className="flex items-center gap-[4px]">
              {item.keys.map((key) => (
                <PiKeycap key={key}>{key}</PiKeycap>
              ))}
              <span className="ml-[4px] text-[11px] text-creator-text-sub">
                {item.label}
              </span>
            </div>
          ))}
        </div>

        {/* The piBlue variant is width:100% — fine for a form, but here it
            would stretch across the footer and run over the legend. */}
        {primaryLabel && onPrimaryAction && (
          <Button
            variant="piBlue"
            disabled={primaryDisabled}
            onClick={onPrimaryAction}
            sx={{ width: "auto", flexShrink: 0, paddingX: "20px" }}
          >
            <span className="flex items-center gap-[10px]">
              {primaryLabel}
              {/* A bare glyph, not a keycap — a bordered cap inside a filled
                  button is two competing containers. */}
              <span className="opacity-[0.72]" aria-hidden>
                ↵
              </span>
            </span>
          </Button>
        )}
      </div>

      {overlay}
    </div>
  );
}
